import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import {
  Wrench,
  Undo2,
  Loader2,
  CalendarClock,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

/**
 * Compact strip showing every tool currently checked out to the logged-in user
 * with a 1-tap "Return" button per row. Pairs with "Reserve for me" on
 * PreparationQueue — same user, opposite direction.
 *
 * Returns to the canonical "Available" status. Power-users who need to flag
 * Maintenance / In Calibration / Damaged should use the full Return dialog on
 * the tool detail page.
 *
 * Hidden when the user has no checked-out tools.
 */
export default function ReleasedByMeStrip() {
  const { user } = useAuth();
  const [tools, setTools] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    if (!user) return;
    try {
      // Pull a generous fetch and filter client-side. The /equipment list
      // endpoint accepts `assigned_to` as a fuzzy match against name+email.
      const params = { status: "Checked Out", assigned_to: user.email || user.name };
      const { data } = await api.get("/equipment", { params });
      const me = (user.email || "").toLowerCase();
      const myName = (user.name || "").toLowerCase();
      // Hard-filter to my exact records (the backend does a case-insensitive
      // regex which can over-match on substrings).
      const mine = (data || []).filter(
        (t) =>
          (t.Assigned_To_Email || "").toLowerCase() === me ||
          (t.Assigned_To || "").toLowerCase() === myName
      );
      setTools(mine);
    } catch {
      /* ignore */
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
    const onExternal = () => load();
    window.addEventListener("rougee:refresh-queue", onExternal);
    return () => window.removeEventListener("rougee:refresh-queue", onExternal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, user?.name]);

  const onReturn = async (tool) => {
    if (busyId) return;
    setBusyId(tool.id);
    try {
      await api.post(`/equipment/${tool.id}/return`, { set_status: "Available" });
      toast.success(`Returned ${tool.Tool_Name}`);
      // Optimistically drop from the strip; reload syncs availability counts elsewhere.
      setTools((curr) => curr.filter((t) => t.id !== tool.id));
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("rougee:refresh-queue"));
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Return failed");
    } finally {
      setBusyId("");
    }
  };

  if (!loaded || tools.length === 0) return null;

  return (
    <div className="mb-5" data-testid="released-by-me-strip">
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-700" />
          <h3 className="font-display uppercase tracking-wide text-sm sm:text-base text-slate-800">
            Released by me
          </h3>
          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300">
            {tools.length}
          </span>
        </div>
        <Link
          to="/equipment?status=Checked+Out"
          data-testid="released-view-all"
          className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900"
        >
          View all
        </Link>
      </div>
      <ul className="space-y-2">
        {tools.map((t) => (
          <li
            key={t.id}
            data-testid={`released-row-${t.Tool_ID}`}
            className="rg-card p-2 sm:p-3 flex items-center gap-3 border-l-[6px] border-amber-400"
          >
            <div className="h-9 w-9 grid place-items-center bg-slate-900 text-amber-400 rounded-sm flex-shrink-0">
              <Wrench strokeWidth={2.5} className="h-4 w-4" />
            </div>
            <Link
              to={`/equipment/${t.id}`}
              className="flex-1 min-w-0 hover:opacity-80"
              data-testid={`released-link-${t.Tool_ID}`}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[11px] font-bold text-slate-600">
                  {t.Tool_ID}
                </span>
                <span className="font-bold text-slate-900 truncate">{t.Tool_Name}</span>
                {t.calibration_state === "overdue" && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-400 rounded-sm">
                    <AlertTriangle className="h-3 w-3" />
                    Cal Overdue
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-600 mt-0.5 flex items-center gap-2 flex-wrap">
                {t.Expected_Return_Date && (
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" />
                    Due back <strong>{t.Expected_Return_Date}</strong>
                  </span>
                )}
                {t.Category && (
                  <span className="text-slate-500">· {t.Category}</span>
                )}
              </div>
            </Link>
            <button
              onClick={() => onReturn(t)}
              disabled={busyId === t.id}
              data-testid={`released-return-btn-${t.Tool_ID}`}
              className="rg-tap px-3 bg-emerald-700 hover:bg-emerald-800 text-white font-bold uppercase tracking-wider text-xs rounded-sm flex items-center gap-1.5 border-2 border-emerald-900 disabled:opacity-60 flex-shrink-0"
              title="Quick-return — sets status to Available"
            >
              {busyId === t.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Undo2 strokeWidth={3} className="h-4 w-4" />
              )}
              Return
            </button>
            <ChevronRight className="h-4 w-4 text-slate-300 hidden sm:block" />
          </li>
        ))}
      </ul>
    </div>
  );
}
